/*
 *   Collects a set of options that are used to populate a new time card in the Timekeeping Entries modal
 */
import { createRef, FC, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import styled from "styled-components"
import SelectorWrapper from "../../SelectorWrapper"
import { referenceablesToValueFormatters } from "../../common/referenceable-value-formatters"
import { getDateTimeFormats } from "../../common/ts-utils"
import { AppDispatch, AsyncThunk, tCurrentUserEmployee, tFeatures } from "../../common/types"
import format from "date-fns-tz/format"
import {
    Checkbox,
    colorAttentionBlue50,
    colorFunctionalBlack,
    colorFunctionalGray50,
    colorLayoutBorder,
    Copy,
    CTAButton,
    DatePicker,
    FormFieldGroup,
    IconAddBorderless,
    Label,
    StandardModal,
} from "@rhumbix/rmbx_design_system_web"
import { tContext, tGroupKeyInfo } from "../custom-dashboards/types"
import { adminAndPmRoles, DATE_ONLY_FORMAT, STORED_DATE_ONLY_FORMAT } from "../../common/constants"
import { MutableSourceData, tSourceData } from "../../dashboard-data/types"
import { GridApi } from "ag-grid-community"
import { CREATE_TIME_CARD_MODAL_CANCELLED, CREATE_TIME_CARD_MODAL_COMPLETED, logUserAmplitudeEvent } from "../../common/amplitude-event-logging"
import { tFilterState } from "../../filters/types"
import { EmployeeHolidayRecord, EmployeePTORecord, iCohort, iCohortEmployee, iEmployee, iTimelineEntryVersion, tCompanyStartStopType, tEntitySelection, tProject } from "../../cached-data/types"
import * as API from "../../api"
import { getSettingsForShiftExtraSchema, populateShiftExtraCostCodeColDef } from "../../new-custom-forms/utils/custom-table-utils"
import { iEmployeeWorkShiftFormSchema } from "../../cached-data/types"
import { getObjectId } from "../../common/ag-grid-ts-utils"
import { loadEntities } from "../../cached-data/actions"
import cloneDeep from "clone-deep"
import { addDays, endOfDay, startOfDay } from "date-fns"
import { getFlagEnabled } from "../../getFlagValue"
import { AbsencePTORowFields, buildPTOCostCodesByBucket, buildPTORowFields, TimekeepingPTORowFields } from "../custom-dashboards/settings-files/pto-requests"
import { selectHolidayObjects, selectPTOFulfillment, selectPTOObjects, selectPTORecordsByEmployeeDate, selectUnfulfilledPTORecordsByDate } from "../../selectors/pto"
import { selectEmployeeIdsByCohortId } from "../../rate-tables/selectors/cohorts"
import SaveTimecardTemplateModal from "./modal-save-timecard-template"
import DeleteTimecardTemplateModal from "./modal-delete-timecard-template"
import TimecardTemplatePicker from "./timecard-template-picker"
import { ApplyTemplateResponse, buildTemplateRows, iTimecardTemplate } from "./timecard-template-utils"

type ContextFields = {
    foreman: number
    shift_start_time: Date | null
    shift_end_time: Date | null
    work_shift_id: Record<string, unknown>
}

/**
 * Given PTO/holiday records for a single employee on a single date, and API-fetched cost codes
 * for the selected projects (keyed by pto_bucket_id), build the initial absence and
 * timekeeping-entry rows that fulfill those requests.
 *
 * Rules:
 *   - ABSENCE_TYPE records produce one absence row per record.
 *   - COST_CODE records are matched against `ptoCostCodesByBucket`:
 *       - 0 matches → no row by default; row with cost_code null if includeUnresolvableCostCodeRows is true
 *       - 1 match  → TK row with that cost code pre-filled
 *       - 2+ matches → TK row with cost_code null (user must choose)
 *
 * @param ptoForEmployee - PTO and holiday records for a single employee on the shift date.
 * @param baseFields - Common fields shared by every generated row.
 * @param ptoCostCodesByBucket - API-fetched cost codes indexed by pto_bucket_id.
 * @param includeUnresolvableCostCodeRows - When true, COST_CODE records with 0 resolvable cost
 *   codes still produce a TK row with cost_code null. Use in flows with a confirmation modal
 *   where the user can fill in the missing cost code (e.g. Duplicate).
 * @returns Object with `absences` and `timekeepingEntries` arrays of partial row objects.
 */
export const buildPTORowsForEmployee = (
    ptoForEmployee: Array<EmployeePTORecord | EmployeeHolidayRecord>,
    baseFields: {
        foreman: number
        shiftStartTime: Date | null
        shiftEndTime: Date | null
        workShift: Record<string, unknown>
    },
    ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map(),
    includeUnresolvableCostCodeRows = false
): {
    absences: Array<AbsencePTORowFields & ContextFields & { isPlaceholder: false }>
    timekeepingEntries: Array<TimekeepingPTORowFields & ContextFields & { cost_code: number | null; isPlaceholder: false }>
} => {
    const absences: Array<AbsencePTORowFields & ContextFields & { isPlaceholder: false }> = []
    const timekeepingEntries: Array<TimekeepingPTORowFields & ContextFields & { cost_code: number | null; isPlaceholder: false }> = []

    ptoForEmployee.forEach(record => {
        const result = buildPTORowFields(record)
        const contextFields = {
            foreman: baseFields.foreman,
            shift_start_time: baseFields.shiftStartTime,
            shift_end_time: baseFields.shiftEndTime,
            work_shift_id: baseFields.workShift,
        }
        if (result.rowType === "absence") {
            absences.push({ ...result.fields, ...contextFields, isPlaceholder: false })
        } else {
            const matchingCostCodes = ptoCostCodesByBucket.get(record.pto_bucket_id) ?? []
            if (matchingCostCodes.length === 0 && !includeUnresolvableCostCodeRows) return
            const resolvedCostCode = matchingCostCodes.length === 1 ? matchingCostCodes[0] : null
            timekeepingEntries.push({
                ...result.fields,
                ...contextFields,
                cost_code: resolvedCostCode?.id ?? null,
                isPlaceholder: false,
            })
        }
    })

    return { absences, timekeepingEntries }
}

/**
 * Fetches PTO and holiday records for any employee IDs not already in the fetch-ref or Redux cache,
 * then dispatches the results into Redux and marks the employee-date keys as fetched.
 *
 * Employees whose `${id}|${shiftDate}` key is already in `reduxKeys` are seeded into `fetchedKeys`
 * and skipped. Employees already in `fetchedKeys` from a prior fetch are also skipped — this
 * covers employees with no records (absent from Redux) so they are not re-requested on every render.
 * Keys are added to `fetchedKeys` before dispatch.
 *
 * @param employeeIds - All employee IDs to consider fetching for.
 * @param shiftDate - YYYY-MM-DD shift date string.
 * @param fetchedKeys - Mutable ref set tracking which employee-date keys have been fetched this session.
 * @returns The PTO and holiday records for the requested employees on `shiftDate`, read from Redux
 *   state after any fetch completes. Safe to use immediately — no re-render needed.
 */
export const fetchPTOForEmployees = (
    employeeIds: number[],
    shiftDate: string,
    fetchedKeys: Set<string>
): AsyncThunk<{ ptoRecords: EmployeePTORecord[]; holidayRecords: EmployeeHolidayRecord[] }> => async (dispatch, getState) => {
    const reduxKeys = selectPTORecordsByEmployeeDate(getState())
    for (const id of employeeIds) {
        if (reduxKeys.has(`${id}|${shiftDate}`)) fetchedKeys.add(`${id}|${shiftDate}`)
    }
    const uncachedIds = employeeIds.filter(id => !fetchedKeys.has(`${id}|${shiftDate}`))
    if (uncachedIds.length > 0) {
        try {
            const [ptoResponse, holidayResponse] = await Promise.all([
                API.getV4Resources("employeePTO", {
                    shift_start_time_0: shiftDate,
                    shift_start_time_1: shiftDate,
                    employee_id: uncachedIds,
                }) as Promise<{ results: EmployeePTORecord[] }>,
                API.getV4Resources("employeeHolidays", {
                    shift_start_time_0: shiftDate,
                    shift_start_time_1: shiftDate,
                    employee_id: uncachedIds,
                }) as Promise<{ results: EmployeeHolidayRecord[] }>,
            ])
            uncachedIds.forEach(id => fetchedKeys.add(`${id}|${shiftDate}`))
            dispatch(
                loadEntities({
                    employeePTO: Object.fromEntries(ptoResponse.results.map(r => [r.id, r])),
                    employeeHolidays: Object.fromEntries(holidayResponse.results.map(r => [r.id, r])),
                })
            )
        } catch {
            // swallow fetch errors — missing PTO data degrades gracefully
        }
    }
    // Read from fresh Redux state so returned records include any newly-fetched data.
    const employeeIdSet = new Set(employeeIds)
    const ptoRecords = Object.values(selectPTOObjects(getState())).filter(r => employeeIdSet.has(r.employee) && r.date === shiftDate)
    const holidayRecords = Object.values(selectHolidayObjects(getState())).filter(r => employeeIdSet.has(r.employee) && r.date === shiftDate)
    return { ptoRecords, holidayRecords }
}

/**
 * Fetches cohort-employee membership records for any cohort IDs not already in the fetch-ref,
 * then dispatches the results into Redux and marks the cohort IDs as fetched.
 *
 * Cohort IDs already in `fetchedIds` are skipped entirely. The ref is seeded *after* dispatch
 * so that callers checking the ref can rely on the data already being present in Redux.
 *
 * @param cohortIds - All cohort IDs to consider fetching for.
 * @param fetchedIds - Mutable ref set tracking which cohort IDs have been fetched this session.
 * @returns The complete, deduplicated list of employee IDs across all requested cohorts, read
 *   from Redux state after any fetch completes. Safe to use immediately — no re-render needed.
 */
export const fetchCohortEmployees = (cohortIds: number[], fetchedIds: Set<number>): AsyncThunk<number[]> => async (dispatch, getState) => {
    const unfetchedIds = cohortIds.filter(id => !fetchedIds.has(id))
    if (unfetchedIds.length > 0) {
        const response = (await API.getV4Resources("cohortEmployees", {
            cohort_ids: unfetchedIds,
            filter_employee_access: true,
        })) as { results: iCohortEmployee[] }
        dispatch(
            loadEntities({
                cohortEmployees: Object.fromEntries(response.results.map((r: iCohortEmployee) => [r.id, r])),
            })
        )
        unfetchedIds.forEach(id => fetchedIds.add(id))
    }
    // Read fresh state after dispatch so the returned IDs include newly-fetched cohort members.
    const employeeIdsByCohortId = selectEmployeeIdsByCohortId(getState())
    return cohortIds.flatMap(id => employeeIdsByCohortId[id] ?? [])
}

export type CreateTimeCardModalProps = {
    // Modal props
    open: boolean
    title?: string
    description?: string
    primaryBtnText?: string
    secondaryBtnOnClick?: () => void
    backgroundCloseEnabled?: boolean
    closeMethod: () => void

    // Additional props
    currentEmployee: tCurrentUserEmployee
    features: tFeatures
    isAdminOrPM: boolean
    setSourceTableLockedColumns: (groupKeyInfo: tGroupKeyInfo[], tkModalInitialData: tSourceData, forceCreateNewTimecard: boolean, isCreateFlow: boolean, filteredProjects: tProject[]) => void
    gridApi: GridApi | any
    context: tContext
    filterState: tFilterState
    referenceableData: tEntitySelection
}

export type CreateTimeCardModalTypes = CreateTimeCardModalProps

const CreateTimeCardModal: FC<CreateTimeCardModalTypes> = props => {
    const defaultShiftStartTime = new Date()
    defaultShiftStartTime.setHours(7)
    defaultShiftStartTime.setMinutes(0)
    defaultShiftStartTime.setSeconds(0)
    defaultShiftStartTime.setMilliseconds(0)
    const defaultShiftEndTime = new Date()
    defaultShiftEndTime.setHours(15)
    defaultShiftEndTime.setMinutes(0)
    defaultShiftEndTime.setSeconds(0)
    defaultShiftEndTime.setMilliseconds(0)

    const [shiftStartTime, setShiftStartTime] = useState<Date | null>(defaultShiftStartTime)
    const [shiftEndTime, setShiftEndTime] = useState<Date | null>(defaultShiftEndTime)
    const [useProjectFilters, setUseProjectFilters] = useState(true)
    const [includeInactive, setIncludeInactive] = useState(false)

    // Ensure that the start date is always earlier than or equal to the end date
    useEffect(() => {
        if (shiftEndTime && shiftStartTime && shiftEndTime < shiftStartTime) {
            // If possible, adjust the date for shiftEndTime while keeping the time,
            // so that you can move a shift date out without having to re-set the times of day
            const updatedShiftEndTime = new Date(shiftStartTime.getTime())
            if (shiftEndTime.getHours() > updatedShiftEndTime.getHours()) updatedShiftEndTime.setHours(shiftEndTime.getHours())
            setShiftEndTime(updatedShiftEndTime)
        } else if (shiftStartTime && shiftEndTime) {
            // Adjust the shift end date to be the same day as the shift start date if you
            const updatedShiftEndTime = new Date(shiftStartTime.getTime())
            if (shiftEndTime.getHours() > updatedShiftEndTime.getHours()) updatedShiftEndTime.setHours(shiftEndTime.getHours())
            setShiftEndTime(updatedShiftEndTime)
        }
    }, [shiftStartTime])

    useEffect(() => {
        if (shiftEndTime && shiftStartTime && shiftEndTime < shiftStartTime) {
            setShiftEndTime(shiftStartTime)
        }
    }, [shiftEndTime])

    // Using "any" as a type here because SelectorWrapper's onChange method has the "any" type as well,
    // as there were some issues resolving a specific type when pulling the selector's option.
    // If a change to SelectorWrapper is made to enforce a specific type, this type should be refactored
    // as well.
    const [foreman, setForeman] = useState<any>(props.currentEmployee)
    const [projects, setProjects] = useState<any>([])
    const [employees, setEmployees] = useState<any>(props.currentEmployee.user_role === "OFFICE_STAFF" ? [props.currentEmployee] : [])
    const [cohorts, setCohorts] = useState<iCohort[]>([])
    const [costCodes, setCostCodes] = useState<any>([])
    const [addCicoSSTs, setAddCicoSSTs] = useState(true)
    const [templates, setTemplates] = useState<iTimecardTemplate[]>([])
    const [selectedTemplate, setSelectedTemplate] = useState<iTimecardTemplate | null>(null)
    const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false)
    const [templatePendingDelete, setTemplatePendingDelete] = useState<iTimecardTemplate | null>(null)
    const [templateApplyResult, setTemplateApplyResult] = useState<ApplyTemplateResponse | null>(null)
    /** Cost code ids to apply once the project change they depend on has committed and cleared costCodes. */
    const pendingTemplateCostCodeIds = useRef<number[] | null>(null)
    const ptoFulfillment = useSelector(selectPTOFulfillment)
    const unfulfilledPTORecordsByDate = useSelector(selectUnfulfilledPTORecordsByDate)
    const employeeIdsByCohortId = useSelector(selectEmployeeIdsByCohortId)
    /** "${employeeId}|${date}" keys for which PTO/holiday records have been fetched into Redux. */
    const fetchedPTOEmployeeDates = useRef<Set<string>>(new Set())
    /** Cohort IDs whose employee lists have been successfully fetched into Redux this session. */
    const fetchedCohortIds = useRef<Set<number>>(new Set())
    const allEmployeeIds = useMemo(() => [...new Set([...employees.map((e: iEmployee) => e.id), ...cohorts.flatMap((c: iCohort) => employeeIdsByCohortId[c.id] ?? [])])], [
        employees,
        cohorts,
        employeeIdsByCohortId,
    ])

    // shouldClearCache tells the selectors that follow it to clear their cache, for example
    // because another selector that is related to it has been updated
    const [shouldClearCache, setShouldClearCache] = useState(false)
    const [shouldClearProjectCache, setShouldClearProjectCache] = useState(false)
    const [companyStartStopTypes, setCompanyStartStopTypes] = useState<tCompanyStartStopType[]>([])
    const [shiftExtraSchemas, setShiftExtraSchemas] = useState<iEmployeeWorkShiftFormSchema[]>([])
    const shiftDateTimeInterval = props.context.currentUser.company_options.timekeeping_increment ?? 30

    // Every time we load projects, clear the caches of the selectors that filter on project IDs
    useEffect(() => {
        setShouldClearCache(true)
        setCostCodes([])
    }, [projects])

    // Runs after the effect above, so it wins the race to set costCodes when a template
    // selection changed both projects and costCodes in the same update.
    useEffect(() => {
        if (pendingTemplateCostCodeIds.current === null) return
        const costCodeIds = pendingTemplateCostCodeIds.current
        pendingTemplateCostCodeIds.current = null
        if (costCodeIds.length === 0) return
        API.getReferenceableDataByResourceAndIds("costCodes", costCodeIds).then(response => {
            setCostCodes(response.results)
        })
    }, [projects])

    useEffect(() => {
        setShouldClearProjectCache(true)
    }, [useProjectFilters, includeInactive])

    useEffect(() => {
        if (!getFlagEnabled("WA-9280-timecard-templates")) return
        API.getTimecardTemplates().then(setTemplates)
    }, [])

    useEffect(() => {
        API.getRowData("companyStartStopTypes").then(results => {
            setCompanyStartStopTypes(results.results)
        })
        API.getRowData("employeeSchemas").then(results => {
            setShiftExtraSchemas(results.results)
        })
    }, [])

    // Eagerly fetch PTO/Holiday records for selected employees so they're ready by save time.
    useEffect(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals") || !shiftStartTime || allEmployeeIds.length === 0) return
        const shiftDate = format(shiftStartTime, STORED_DATE_ONLY_FORMAT)

        props.context.dispatch(fetchPTOForEmployees(allEmployeeIds, shiftDate, fetchedPTOEmployeeDates.current))
    }, [shiftStartTime, allEmployeeIds])

    // Eagerly fetch employee IDs for newly-selected cohorts so they're ready by save time.
    useEffect(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals") || cohorts.length === 0) return
        const cohortIds: number[] = cohorts.map((c: iCohort) => c.id)
        props.context.dispatch(fetchCohortEmployees(cohortIds, fetchedCohortIds.current))
    }, [cohorts])

    // A callback function that selectors call after they clear their cache
    const updateClearCache = () => {
        setShouldClearCache(false)
    }

    const updateClearProjectCache = () => {
        setShouldClearProjectCache(false)
    }

    // Pre-populate setup selections from a chosen template. The template's entries/shift_extras/
    // absences are applied later, at save time, via the apply action (see createInitialData).
    const handleSelectTemplate = (template: iTimecardTemplate | null) => {
        setSelectedTemplate(template)
        setTemplateApplyResult(null)
        if (!template) return

        const { projects: projectIds, employees: employeeIds, project_cost_codes: costCodeIds } = template.contents.setup
        pendingTemplateCostCodeIds.current = costCodeIds

        Promise.all([API.getReferenceableDataByResourceAndIds("projects", projectIds), API.getReferenceableDataByResourceAndIds("employees", employeeIds)]).then(
            ([projectsResponse, employeesResponse]) => {
                setProjects(projectsResponse.results)
                setEmployees(employeesResponse.results)
            }
        )
    }

    const handleTemplateSaved = (template: iTimecardTemplate) => {
        setTemplates([...templates, template])
    }

    const handleTemplateUpdated = (template: iTimecardTemplate) => {
        setTemplates(templates.map(t => (t.id === template.id ? template : t)))
        if (selectedTemplate?.id === template.id) setSelectedTemplate(template)
    }

    const handleTemplateDeleted = (templateId: number) => {
        setTemplates(templates.filter(template => template.id !== templateId))
        if (selectedTemplate?.id === templateId) {
            setSelectedTemplate(null)
            setTemplateApplyResult(null)
        }
    }

    // Cost codes are optional only when every selected employee has an unfulfilled PTO/holiday
    // record for the shift date (the timecard exists solely to fulfill PTO). If even one selected
    // employee has no unfulfilled PTO, cost codes stay required so their working time can be coded.
    const allSelectedEmployeesHaveUnfulfilledPTO = useMemo(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals") || !shiftStartTime || allEmployeeIds.length === 0) return false
        const shiftDate = format(shiftStartTime, STORED_DATE_ONLY_FORMAT)
        const records = unfulfilledPTORecordsByDate.get(shiftDate) ?? []
        const employeesWithUnfulfilledPTO = new Set<number>(records.map(r => r.employee))
        return allEmployeeIds.every((id: number) => employeesWithUnfulfilledPTO.has(id))
    }, [shiftStartTime, allEmployeeIds, unfulfilledPTORecordsByDate])

    // These formatters format the options in the selectors
    const employeeValueFormatters = referenceablesToValueFormatters["employees"] || {}
    const projectValueFormatters = referenceablesToValueFormatters["projects"] || {}
    const costCodeValueFormatters = referenceablesToValueFormatters["costCodes"] || {}
    const cohortValueFormatters = referenceablesToValueFormatters["cohorts"] || {}

    const timeCardOwnerFilter = {
        exclude_user_role: "WORKER",
    }
    const datetimeFormat = getDateTimeFormats("datetime", false)
    const innerRef = createRef<HTMLDivElement>()

    const populateInitialRowData = (
        shiftDate: string,
        workShift: Record<string, any>,
        cicoData?: iTimelineEntryVersion[],
        cohortEmployees?: number[],
        ptoRecords: EmployeePTORecord[] = [],
        holidayRecords: EmployeeHolidayRecord[] = [],
        ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map(),
        templateRows: ReturnType<typeof buildTemplateRows> | null = null
    ) => {
        const initialRowData: MutableSourceData = {
            timekeepingEntries: [],
            absences: [],
            employeeEntries: [],
            ewsStartStopTimes: [],
            timelineEntryVersions: cicoData,
        }
        const employeeList = new Set([...(cohortEmployees || []), ...employees.map((e: iEmployee) => getObjectId(e))])

        // Employees with rows supplied by the applied template skip the standard placeholder
        // rows below -- the template's rows fulfill the timecard for them, same as PTO rows do.
        const templateEmployeeIds = new Set<number>([
            ...(templateRows?.timekeepingEntries.map(row => row.employee as number) ?? []),
            ...(templateRows?.employeeEntries.map(row => row.employee as number) ?? []),
            ...(templateRows?.absences.map(row => row.employee as number) ?? []),
        ])

        if (!props.features.cico) {
            const missingReferenceables = Array.from(employeeList).filter((eId: number) => !props.referenceableData.employees[eId])
            if (missingReferenceables.length) {
                API.getReferenceableDataByResourceAndIds("employees", missingReferenceables).then(response => {
                    const empPayload = cloneDeep(props.referenceableData.employees)
                    response.results.forEach((emp: iEmployee) => {
                        empPayload[emp.id as number] = emp
                    })
                    props.context.dispatch(
                        loadEntities({
                            employees: cloneDeep(empPayload),
                        })
                    )
                })
            }
        }

        // Pre-group PTO records by employee so the per-employee lookup below is O(1) instead of O(n).
        const ptoByEmployee = getFlagEnabled("WA-9197-pto-accruals")
            ? ([...ptoRecords, ...holidayRecords] as Array<EmployeePTORecord | EmployeeHolidayRecord>).reduce((map, r) => {
                  if (!map[r.employee]) {
                      map[r.employee] = []
                  }
                  map[r.employee].push(r)
                  return map
              }, {} as Record<number, Array<EmployeePTORecord | EmployeeHolidayRecord>>)
            : null

        // Prepare to populate the table with every possible combination of the employees and cost codes
        // that were specified. Users are required to pick at least one of each of these entities,
        // so it's safe just to nest these forEach loops without handling cases for empty arrays
        employeeList.forEach((employee: any) => {
            // TODO: Some of these fields - specifically the adjusted_minutes_* fields - should be populated
            // in addSourceDataRow, which would normally go through
            // and set defaults for all applicable columns on a new row.
            // However, because we're doing all of this before the table fully loads, the colDefs are missing
            // some information (specifically the cellEditorParams) and so those defaults cannot be applied.
            // I hacked in defaults for the three relevant fields but I kind of hate this - it makes the code
            // brittle and feels unnecessary. It would be nice at least to find another way to do this.
            const employeeId = getObjectId(employee)

            // Build any auto-added PTO/holiday rows for this employee up front. If at least one row
            // is produced, this timecard is fulfilling a PTO request for the employee, so we skip
            // the standard Timekeeping placeholder rows below — they would be redundant.
            const ptoForEmployee = ptoByEmployee?.[employeeId] ?? []
            const ptoRows =
                ptoForEmployee.length > 0
                    ? buildPTORowsForEmployee(ptoForEmployee, { foreman: foreman.id, shiftStartTime, shiftEndTime, workShift }, ptoCostCodesByBucket)
                    : { absences: [], timekeepingEntries: [] }
            const hasAutoAddedPTORow = ptoRows.absences.length > 0 || ptoRows.timekeepingEntries.length > 0
            const hasTemplateRowForEmployee = templateEmployeeIds.has(employeeId)

            costCodes.forEach((costCode: any) => {
                // Skip the standard placeholder row when a PTO/holiday row is being auto-added, or
                // when the applied template already supplies this employee's rows — either way the
                // placeholder would be redundant.
                if (hasAutoAddedPTORow || hasTemplateRowForEmployee) return

                const row: Record<string, any> = {
                    employee: employeeId,
                    cost_code: costCode.id,
                    shift_start_time: shiftStartTime,
                    shift_end_time: shiftEndTime,
                    date: shiftDate,
                    foreman: foreman.id,
                    adjusted_minutes_st: 0,
                    adjusted_minutes_ot: 0,
                    adjusted_minutes_dt: 0,
                    work_shift_id: workShift,
                }

                initialRowData.timekeepingEntries?.push(row)

                // Add any per employee/cost code shift extra placeholders
                shiftExtraSchemas
                    .filter(se => se.create_placeholder === "PER_EMPLOYEE_COST_CODE")
                    .forEach(se => {
                        const shiftExtraSettings = getSettingsForShiftExtraSchema(se, props.context)
                        const baseRecord: Record<string, any> = {
                            foreman: foreman.id,
                            employee: employeeId,
                            date: shiftDate,
                            schema: se,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                        }
                        if (getFlagEnabled("WA-9382-create-timecard-fixes") && projects.length === 1) baseRecord.project = projects[0]
                        // We need to find any cost code type fields and fill them in with the
                        // current cost code.
                        const shiftExtraFields = populateShiftExtraCostCodeColDef(shiftExtraSettings, costCode.id)
                        initialRowData.employeeEntries?.push({ ...baseRecord, ...shiftExtraFields })
                    })
            })
            const employeeCico = cicoData ? cicoData.filter(cd => getObjectId(cd.employee) === getObjectId(employee)) : []
            if (employeeCico.length) {
                const cicoEventMap: Record<string, tCompanyStartStopType> = {}
                companyStartStopTypes.forEach(sst => {
                    switch (sst.name) {
                        case "Break":
                            cicoEventMap["START_BREAK"] = sst
                            break
                        case "Meal":
                            cicoEventMap["START_MEAL"] = sst
                            break
                        case "Shift":
                            cicoEventMap["CLOCK_IN"] = sst
                            break
                        default:
                            break
                    }
                })
                const shiftDuration = employeeCico.reduce((agg, timelineEntry) => {
                    agg += timelineEntry.duration || 0
                    return agg
                }, 0)
                employeeCico.forEach((timelineEntry: iTimelineEntryVersion) => {
                    if (timelineEntry.entry_type in cicoEventMap) {
                        const duration = timelineEntry.entry_type === "CLOCK_IN" ? shiftDuration : timelineEntry.duration
                        const endTime = duration ? new Date(new Date(timelineEntry.entered_time).getTime() + duration * 60000).toISOString() : ""
                        initialRowData.ewsStartStopTimes!.push({
                            foreman: foreman.id,
                            employee: employeeId,
                            company_start_stop_type: cicoEventMap[timelineEntry.entry_type],
                            start_stop_type: cicoEventMap[timelineEntry.entry_type].name,
                            date: shiftDate,
                            start_time: timelineEntry.entered_time,
                            stop_time: endTime,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                            isPlaceholder: false,
                            cico_diff: 0,
                        })
                    }
                })
            } else
                companyStartStopTypes.forEach(sst => {
                    const numPlaceholders = sst.num_placeholders || 0
                    for (let i = 0; i < numPlaceholders; i++)
                        initialRowData.ewsStartStopTimes?.push({
                            foreman: foreman.id,
                            employee: employeeId,
                            company_start_stop_type: sst,
                            start_stop_type: sst.name,
                            date: shiftDate,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                            cico_diff: 0,
                        })
                })
            shiftExtraSchemas
                .filter(se => se.create_placeholder === "PER_EMPLOYEE")
                .forEach(se => {
                    const baseRecord: Record<string, any> = {
                        foreman: foreman.id,
                        employee: employeeId,
                        date: shiftDate,
                        schema: se,
                        shift_start_time: shiftStartTime,
                        shift_end_time: shiftEndTime,
                        work_shift_id: workShift,
                    }
                    if (getFlagEnabled("WA-9382-create-timecard-fixes") && projects.length === 1) baseRecord.project = projects[0]
                    initialRowData.employeeEntries?.push(baseRecord)
                })

            // Append the auto-added PTO/holiday rows built above. These rows have actual
            // hours/absence-type filled in and replace the standard placeholder rows that were
            // skipped for this employee.
            ptoRows.absences.forEach(row => initialRowData.absences?.push(row))
            ptoRows.timekeepingEntries.forEach(row => initialRowData.timekeepingEntries?.push(row))
        })

        // Append the applied template's rows. These are exact rows (not a placeholder cross
        // product), so they're built once up front rather than per employee/cost code.
        if (templateRows) {
            templateRows.timekeepingEntries.forEach(row => initialRowData.timekeepingEntries?.push(row))
            templateRows.employeeEntries.forEach(row => initialRowData.employeeEntries?.push(row))
            templateRows.absences.forEach(row => initialRowData.absences?.push(row))
        }

        return initialRowData
    }

    const setGroupKeyInfo = (initialRowData: MutableSourceData, workShift: Record<string, any>) => {
        // TODO: Some of this repeats work that can be done in processTimekeepingModalValues - consider
        // refactoring that
        const foremanColDef = props.gridApi.getColumn("/foreman").colDef
        const dateColDef = props.gridApi.getColumn("/date").colDef
        const groupKeyInfo = [
            {
                colDef: dateColDef,
                value: format(shiftStartTime ?? new Date(), STORED_DATE_ONLY_FORMAT),
            },
            {
                colDef: foremanColDef,
                value: foreman,
            },
        ]

        const workshiftColDef = props.gridApi.getColumn("/work_shift_id").colDef
        groupKeyInfo.push({
            colDef: workshiftColDef,
            value: workShift,
        })

        // Open the timekeeping modal, passing in the prepopulated rows
        props.setSourceTableLockedColumns(groupKeyInfo, initialRowData as MutableSourceData, true, true, projects)

        logUserAmplitudeEvent(CREATE_TIME_CARD_MODAL_COMPLETED, {})

        props.closeMethod()
    }

    const saveTimecard = async (templateApplyMode: "all" | "valid_only") => {
        const shiftDate = format(shiftStartTime ?? new Date(), STORED_DATE_ONLY_FORMAT)
        const workShift = {
            start_time: shiftStartTime,
            end_time: shiftEndTime,
            foreman: foreman,
            foreman_id: foreman.id,
            projects: projects,
            date: shiftDate,
            tempId: 1, // This is a little bit of a hack so that we can edit the timecard before it's saved
            // in the create timecard flow. this will go away when we allow for unsaved timecards in the
            // selector
        }

        if (cohorts.length) {
            if (getFlagEnabled("WA-9197-pto-accruals")) {
                const cohortIds = cohorts.map((c: iCohort) => c.id)
                const cohortEmployees = await props.context.dispatch(fetchCohortEmployees(cohortIds, fetchedCohortIds.current))
                const allIds = [...new Set([...cohortEmployees, ...employees.map((e: iEmployee) => e.id)])]
                const ptoData = await props.context.dispatch(fetchPTOForEmployees(allIds, shiftDate, fetchedPTOEmployeeDates.current))
                createInitialData(shiftDate, workShift, templateApplyMode, cohortEmployees, ptoData)
            } else {
                API.getV4Resources("cohortEmployees", {
                    cohort_ids: cohorts.map((c: iCohort) => c.id),
                    filter_employee_access: true,
                }).then(response => {
                    createInitialData(
                        shiftDate,
                        workShift,
                        templateApplyMode,
                        response.results.map((r: iCohortEmployee) => r.employee)
                    )
                })
            }
        } else {
            if (getFlagEnabled("WA-9197-pto-accruals")) {
                const ptoData = await props.context.dispatch(
                    fetchPTOForEmployees(
                        employees.map((e: iEmployee) => e.id),
                        shiftDate,
                        fetchedPTOEmployeeDates.current
                    )
                )
                createInitialData(shiftDate, workShift, templateApplyMode, undefined, ptoData)
            } else {
                createInitialData(shiftDate, workShift, templateApplyMode)
            }
        }
    }

    const onClickSave = () => saveTimecard("all")

    // Retries the save after the user has seen the template's problems/warnings and chosen to
    // proceed anyway, dropping whatever the template couldn't apply.
    const onClickContinueWithTemplateIssues = () => saveTimecard("valid_only")

    const createInitialData = async (
        shiftDate: string,
        workShift: Record<string, any>,
        templateApplyMode: "all" | "valid_only",
        cohortEmployees?: number[],
        ptoData?: { ptoRecords: EmployeePTORecord[]; holidayRecords: EmployeeHolidayRecord[] }
    ) => {
        const cicoResponse = await (addCicoSSTs
            ? (API.getV4Resources("timelineEntryVersions", {
                  shift_start_time_0: shiftDate,
                  shift_start_time_1: shiftDate,
                  for_dashboard: true,
                  employee_id: [employees.map((e: iEmployee) => e.id)],
              }) as Promise<{ results: iTimelineEntryVersion[] }>)
            : null)

        // Filter out already-fulfilled requests so we only pre-populate rows for unfulfilled ones.
        // ptoData was fetched and seeded into Redux by fetchPTOForEmployees before save.
        let ptoRecords: EmployeePTORecord[] = []
        let holidayRecords: EmployeeHolidayRecord[] = []
        if (ptoData) {
            ptoRecords = ptoData.ptoRecords.filter(r => !ptoFulfillment[String(r.id)])
            holidayRecords = ptoData.holidayRecords.filter(r => !ptoFulfillment[String(r.id)])
        }

        // For COST_CODE bucket PTO records, search for matching cost codes on the selected
        // projects so the user doesn't have to manually select them.
        let ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map()
        if (getFlagEnabled("WA-9197-pto-accruals")) {
            try {
                ptoCostCodesByBucket = await (props.context.dispatch as AppDispatch)(
                    buildPTOCostCodesByBucket(
                        [...ptoRecords, ...holidayRecords],
                        projects.map((p: tProject) => p.id),
                        costCodes
                    )
                )
            } catch {
                // A cost-code lookup failure is non-fatal; the user can select cost codes manually.
            }
        }

        let templateRows: ReturnType<typeof buildTemplateRows> | null = null
        if (selectedTemplate) {
            const applyResponse: ApplyTemplateResponse = await API.applyTimecardTemplate(selectedTemplate.id, shiftDate, templateApplyMode)
            if (applyResponse.payload) {
                setTemplateApplyResult(null)
                const projectsResponse = await API.getReferenceableDataByResourceAndIds("projects", applyResponse.payload.projectList)
                const projectsById: Record<number, tProject> = {}
                projectsResponse.results.forEach((project: tProject) => {
                    projectsById[project.id as number] = project
                })
                templateRows = buildTemplateRows(applyResponse.payload, { foreman: foreman.id, workShift }, shiftExtraSchemas, projectsById)
            } else if (applyResponse.problems.length > 0) {
                // Genuine unavailable items blocked the apply -- stop here and let the problems
                // banner (rendered below, driven by templateApplyResult) offer to continue without
                // the dropped items.
                setTemplateApplyResult(applyResponse)
                return
            }
            // else: the template has nothing to replay (e.g. a setup-only template with no saved
            // entries/shift_extras/absences, since "Save Template" here can only capture
            // projects/employees/cost codes). That's not a problem -- its setup already
            // pre-populated the selectors above, so just continue with the standard placeholder
            // rows as if no template were selected.
        }

        const initialRowData = populateInitialRowData(shiftDate, workShift, cicoResponse?.results.reverse(), cohortEmployees, ptoRecords, holidayRecords, ptoCostCodesByBucket, templateRows)
        setGroupKeyInfo(initialRowData, workShift)
    }

    const onClickCancel = () => {
        logUserAmplitudeEvent(CREATE_TIME_CARD_MODAL_CANCELLED, {})

        props.closeMethod()
    }

    const getLabelText = (elementCount: number, singularEntityName: string, pluralEntityName: string) => {
        return elementCount ? `${pluralEntityName} (${elementCount})` : pluralEntityName
    }

    return (
        <>
            <StandardModal
                {...props}
                title={"Create a Time Card"}
                description={"Select the projects, workers, and cost codes for the time card you'd like to create. " + "The next step will be to add hours to the time card."}
                width={"90vw"}
                theme={"formModal"}
                autoClose={false}
                primaryBtnColor={colorAttentionBlue50}
                primaryBtnOnClick={onClickSave}
                primaryBtnDisabled={!(projects.length > 0 && (employees.length > 0 || cohorts.length > 0) && (costCodes.length > 0 || allSelectedEmployeesHaveUnfulfilledPTO) && foreman)}
                secondaryBtnOnClick={onClickCancel}
            >
                <Wrapper scrollable={true}>
                    {getFlagEnabled("WA-9280-timecard-templates") && (
                        <>
                            <hr style={{ margin: 0 }} />
                            <div style={{ borderBottom: `1px solid ${colorLayoutBorder}`, padding: 4 }}>
                                <TemplatePickerRow>
                                    <TimecardTemplatePicker
                                        id="timecard-template-selector"
                                        templates={templates}
                                        selectedTemplate={selectedTemplate}
                                        onSelect={handleSelectTemplate}
                                        onRequestDelete={setTemplatePendingDelete}
                                    />
                                    <TemplatePickerSeparator />
                                    <AddTemplateButton
                                        type="button"
                                        disabled={!(projects.length > 0 || employees.length > 0 || cohorts.length > 0 || costCodes.length > 0)}
                                        onClick={() => setSaveTemplateModalOpen(true)}
                                    >
                                        <IconAddBorderless width={12} height={12} />
                                        Save Template
                                    </AddTemplateButton>
                                </TemplatePickerRow>
                                {templateApplyResult && !templateApplyResult.payload && (
                                    <TemplateIssuesBanner>
                                        <Copy>
                                            {templateApplyResult.problems.length > 0
                                                ? `${templateApplyResult.problems.length} item(s) from this template are unavailable for this date.`
                                                : "Nothing from this template is available for this date."}
                                        </Copy>
                                        {templateApplyResult.problems.length > 0 && <CTAButton text="Continue Without Unavailable Items" type="button" onClick={onClickContinueWithTemplateIssues} />}
                                    </TemplateIssuesBanner>
                                )}
                            </div>
                        </>
                    )}
                    <FormFieldGroup>
                        <Label htmlFor="time-card-owner-selector" isRequired={true}>
                            Time Card Owner
                        </Label>
                        <SelectorWrapper
                            id="time-card-owner-selector"
                            isDisabled={!adminAndPmRoles.has(props.currentEmployee.user_role)}
                            innerRef={innerRef}
                            isMulti={false}
                            resourceName={"employees"}
                            onChange={setForeman}
                            value={foreman}
                            filters={timeCardOwnerFilter}
                            valueFormatter={employeeValueFormatters.valueFormatter}
                            titleFormatter={employeeValueFormatters.titleFormatter}
                            primarySubtitleFormatter={employeeValueFormatters.primarySubtitleFormatter}
                            secondarySubtitleFormatter={employeeValueFormatters.secondarySubtitleFormatter}
                        />
                    </FormFieldGroup>
                    <FormFieldGroup>
                        <div style={{ marginTop: 10, marginBottom: 0 }}>
                            <Label htmlFor="date" isRequired={true}>
                                Shift Date
                            </Label>
                            {props.features.disable_work_shift_start_stop_times ? (
                                <div className="date-pickers" style={{ marginBottom: 16, width: 250 }}>
                                    <DatePicker
                                        id="work-shift-date"
                                        className="form-control date-filter-input"
                                        dateFormat={DATE_ONLY_FORMAT}
                                        onChange={(date: Date) => setShiftStartTime(date)}
                                        selected={shiftStartTime}
                                        showTimeSelect={false}
                                    />
                                </div>
                            ) : (
                                <div className="date-pickers">
                                    <DatePicker
                                        id="start-date"
                                        className="form-control date-filter-input"
                                        dateFormat={datetimeFormat.dateFormat}
                                        onChange={(date: Date) => setShiftStartTime(date)}
                                        selected={shiftStartTime}
                                        timeIntervals={shiftDateTimeInterval}
                                        showTimeSelect={true}
                                    />{" "}
                                    <div className="date-filter-to">
                                        <p> — </p>
                                    </div>
                                    <DatePicker
                                        id="end-date"
                                        className="form-control date-filter-input"
                                        dateFormat={datetimeFormat.dateFormat}
                                        onChange={(date: Date) => setShiftEndTime(date)}
                                        selected={shiftEndTime}
                                        minDate={getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime ? startOfDay(shiftStartTime) : undefined}
                                        minTime={getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime ? startOfDay(shiftStartTime) : undefined}
                                        maxTime={getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime ? endOfDay(shiftStartTime) : undefined}
                                        maxDate={getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime ? addDays(shiftStartTime, 1) : undefined}
                                        timeIntervals={shiftDateTimeInterval}
                                        showTimeSelect={true}
                                    />
                                </div>
                            )}
                            {props.features.cico && (
                                <AddCicoSSTs>
                                    <div style={{ marginRight: 10 }}>
                                        <Checkbox id={"add-cico-ssts"} checked={addCicoSSTs} onChange={() => setAddCicoSSTs(!addCicoSSTs)} />
                                    </div>
                                    <InheritFiltersLabel>
                                        <Label htmlFor={"add-cico-ssts"}>Add Shift & Break rows for CI/CO data</Label>
                                    </InheritFiltersLabel>
                                </AddCicoSSTs>
                            )}
                        </div>
                    </FormFieldGroup>
                    <FormFieldGroup>
                        <Label htmlFor="projects-selector" isRequired={true}>
                            {getLabelText(projects.length, "Project", "Projects")}
                        </Label>
                        <SelectorWrapper
                            id="projects-selector"
                            innerRef={innerRef}
                            isMulti={true}
                            resourceName={"projects"}
                            onChange={setProjects}
                            value={projects}
                            filters={
                                useProjectFilters
                                    ? {
                                          group_id: props.filterState.groupId,
                                          project_status: includeInactive ? null : "ACTIVE",
                                          project_id: props.filterState.projectId,
                                      }
                                    : {
                                          project_status: !includeInactive ? "ACTIVE" : null,
                                      }
                            }
                            shouldClearCache={shouldClearProjectCache}
                            updateClearCache={updateClearProjectCache}
                            valueFormatter={projectValueFormatters.valueFormatter}
                            titleFormatter={projectValueFormatters.titleAndStatusFormatter}
                            primarySubtitleFormatter={projectValueFormatters.primarySubtitleFormatter}
                            secondarySubtitleFormatter={projectValueFormatters.secondarySubtitleFormatter}
                        />

                        <ProjectLabelContainer>
                            <InheritFilters>
                                <InheritFiltersLabel>
                                    <Checkbox id={"include-inactive-projects"} checked={includeInactive} onChange={() => setIncludeInactive(!includeInactive)} />
                                </InheritFiltersLabel>
                                <InheritFiltersLabel>
                                    <Label htmlFor={"use-project-filters"}>Include Inactive Projects</Label>
                                </InheritFiltersLabel>
                                <InheritFiltersLabel>
                                    <Checkbox id={"use-project-filters"} checked={useProjectFilters} onChange={() => setUseProjectFilters(!useProjectFilters)} />
                                </InheritFiltersLabel>
                                <InheritFiltersLabel>
                                    <Label htmlFor={"use-project-filters"}>Apply Filters from Weekly View</Label>
                                </InheritFiltersLabel>
                            </InheritFilters>
                        </ProjectLabelContainer>
                    </FormFieldGroup>
                    <FormFieldGroup>
                        <Label htmlFor="cohorts-selector" isRequired={false}>
                            {getLabelText(cohorts.length, "Cohort", "Cohorts")}
                        </Label>
                        <SelectorWrapper
                            id="cohorts-selector"
                            innerRef={innerRef}
                            isDisabled={props.currentEmployee.user_role === "OFFICE_STAFF"}
                            isMulti={true}
                            menuPlacement={"top"}
                            resourceName={"cohorts"}
                            onChange={options => setCohorts(options as iCohort[])}
                            value={cohorts}
                            filters={{ remove_empty: true }}
                            valueFormatter={cohortValueFormatters.valueFormatter}
                            titleFormatter={cohortValueFormatters.titleFormatter}
                        />
                    </FormFieldGroup>
                    <FormFieldGroup>
                        <Label htmlFor="employees-selector" isRequired={true}>
                            {getLabelText(employees.length, "Employee", "Employees")}
                        </Label>
                        <SelectorWrapper
                            id="employees-selector"
                            innerRef={innerRef}
                            isMulti={true}
                            resourceName={"employees"}
                            onChange={setEmployees}
                            value={employees}
                            filters={[]}
                            menuPlacement={"top"}
                            valueFormatter={employeeValueFormatters.valueFormatter}
                            titleFormatter={employeeValueFormatters.titleFormatter}
                            primarySubtitleFormatter={employeeValueFormatters.primarySubtitleFormatter}
                            secondarySubtitleFormatter={employeeValueFormatters.secondarySubtitleFormatter}
                        />
                    </FormFieldGroup>
                    <FormFieldGroup>
                        <Label htmlFor="cost-code-selector" isRequired={!allSelectedEmployeesHaveUnfulfilledPTO}>
                            {getLabelText(costCodes.length, "Cost Code", "Cost Codes")}
                        </Label>
                        <SelectorWrapper
                            id="cost-code-selector"
                            innerRef={innerRef}
                            isMulti={true}
                            resourceName={"costCodes"}
                            onChange={setCostCodes}
                            value={costCodes}
                            filters={{
                                minimal_project_detail: "true",
                                project_id: [projects.map((proj: any) => proj.id)],
                            }}
                            menuPlacement={"top"}
                            valueFormatter={costCodeValueFormatters.valueFormatter}
                            titleFormatter={costCodeValueFormatters.titleFormatter}
                            primarySubtitleFormatter={costCodeValueFormatters.primarySubtitleFormatter}
                            secondarySubtitleFormatter={costCodeValueFormatters.secondarySubtitleFormatter}
                            shouldClearCache={shouldClearCache}
                            updateClearCache={updateClearCache}
                        />
                    </FormFieldGroup>
                </Wrapper>
            </StandardModal>
            {getFlagEnabled("WA-9280-timecard-templates") && (
                <SaveTimecardTemplateModal
                    open={saveTemplateModalOpen}
                    closeMethod={() => setSaveTemplateModalOpen(false)}
                    templates={templates}
                    onSaved={handleTemplateSaved}
                    onUpdated={handleTemplateUpdated}
                    projects={projects}
                    employees={employees}
                    costCodes={costCodes}
                    shiftStartTime={shiftStartTime}
                    shiftEndTime={shiftEndTime}
                />
            )}
            {getFlagEnabled("WA-9280-timecard-templates") && (
                <DeleteTimecardTemplateModal template={templatePendingDelete} closeMethod={() => setTemplatePendingDelete(null)} onDeleted={handleTemplateDeleted} />
            )}
        </>
    )
}

interface WrapperProps {
    scrollable: boolean
}

const Wrapper: FC<WrapperProps> = ({ children, scrollable }) => {
    if (scrollable) {
        return (
            <ScrollableWrapper>
                <ScrollPadding>{children}</ScrollPadding>
            </ScrollableWrapper>
        )
    }
    return <div style={{ paddingTop: "16px" }}>{children}</div>
}

export default CreateTimeCardModal

const ProjectLabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
    margin-bottom: -15px;
`

const InheritFilters = styled.div`
    display: flex;
`

const InheritFiltersLabel = styled.div`
    margin-right: 8px;
`

const ScrollableWrapper = styled.div`
    padding-top: 16px;
    padding-right: 16px;
    max-height: 90vh;
    overflow-y: scroll;
    overflow-x: clip;
`

const ScrollPadding = styled.div`
    padding-bottom: 5px;
`

const AddCicoSSTs = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 15px;
    margin-bottom: -15px;
`

const TemplatePickerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 12px;
`

const TemplatePickerSeparator = styled.div`
    align-self: stretch;
    margin: 4px 0;
    width: 1px;
    background-color: ${colorLayoutBorder};
`

const TemplateIssuesBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
`

const AddTemplateButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 0;
    color: ${colorFunctionalBlack};
    font-family: Roboto;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;

    &:disabled {
        color: ${colorFunctionalGray50};
        cursor: not-allowed;
    }
`
